import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material'
import {Warning} from '@mui/icons-material'

interface IsFormCompletedModalProps {
  open: boolean
  onClose: () => void
  title?: string
  description?: string
  requiredFields?: string[]
  closeButtonText?: string
  formData?: any
  formType?: 'nursesNotes'
}

const validateFormCompletion = (
  formData: any,
  formType: string = 'nursesNotes'
): {isValid: boolean; missingFields: string[]} => {
  const missingFields: string[] = []

  if (formType === 'nursesNotes') {
    if (
      !formData.noteType ||
      (typeof formData.noteType === 'string' && !formData.noteType.trim())
    ) {
      missingFields.push('Note Type')
    }

    if (
      !formData.visitType ||
      (typeof formData.visitType === 'string' && !formData.visitType.trim())
    ) {
      missingFields.push('Visit Type')
    }

    if (!formData.therapy || (typeof formData.therapy === 'string' && !formData.therapy.trim())) {
      missingFields.push('Therapy')
    }

    if (!formData.vitalSigns) {
      missingFields.push('Vital Signs details')
    } else {
      const vitalSigns = formData.vitalSigns
      if (!vitalSigns.bp || (typeof vitalSigns.bp === 'string' && !vitalSigns.bp.trim())) {
        missingFields.push('Blood Pressure in Vital Signs')
      }
      if (!vitalSigns.pulse || (typeof vitalSigns.pulse === 'string' && !vitalSigns.pulse.trim())) {
        missingFields.push('Pulse in Vital Signs')
      }
      if (!vitalSigns.temp || (typeof vitalSigns.temp === 'string' && !vitalSigns.temp.trim())) {
        missingFields.push('Temperature in Vital Signs')
      }
      if (!vitalSigns.pain || (typeof vitalSigns.pain === 'string' && !vitalSigns.pain.trim())) {
        missingFields.push('Pain Level in Vital Signs')
      }
      if (!vitalSigns.rr || (typeof vitalSigns.rr === 'string' && !vitalSigns.rr.trim())) {
        missingFields.push('Respiratory Rate in Vital Signs')
      }
    }

    if (!formData.assessment) {
      missingFields.push('Assessment details')
    } else {
      const assessment = formData.assessment

      if (!assessment.neuro || (typeof assessment.neuro === 'string' && !assessment.neuro.trim())) {
        missingFields.push('Neurological Assessment')
      }
      if (!assessment.skin || (typeof assessment.skin === 'string' && !assessment.skin.trim())) {
        missingFields.push('Skin Assessment')
      }
      if (!assessment.heent || (typeof assessment.heent === 'string' && !assessment.heent.trim())) {
        missingFields.push('HEENT Assessment')
      }
      if (
        !assessment.respiratory ||
        (typeof assessment.respiratory === 'string' && !assessment.respiratory.trim())
      ) {
        missingFields.push('Respiratory Assessment')
      }
      if (!assessment.gu || (typeof assessment.gu === 'string' && !assessment.gu.trim())) {
        missingFields.push('Genitourinary Assessment')
      }
      if (!assessment.cardiac || (typeof assessment.cardiac === 'string' && !assessment.cardiac.trim())) {
        missingFields.push('Cardiac Assessment')
      }

      if (!assessment.gi) {
        missingFields.push('Gastrointestinal Assessment')
      } else {
        if (
          !assessment.gi.lastBm ||
          (typeof assessment.gi.lastBm === 'string' && !assessment.gi.lastBm.trim())
        ) {
          missingFields.push('Last Bowel Movement')
        }
        if (
          !assessment.gi.weight ||
          !assessment.gi.weight.value ||
          (typeof assessment.gi.weight.value === 'string' && !assessment.gi.weight.value.trim())
        ) {
          missingFields.push('Weight')
        }
      }

      if (
        !assessment.patientConcerns ||
        (typeof assessment.patientConcerns === 'string' && !assessment.patientConcerns.trim())
      ) {
        missingFields.push('Patient Concerns')
      }
      if (!assessment.notes || (typeof assessment.notes === 'string' && !assessment.notes.trim())) {
        missingFields.push('Assessment Notes')
      }
      if (!assessment.dme || (typeof assessment.dme === 'string' && !assessment.dme.trim())) {
        missingFields.push('DME (Durable Medical Equipment)')
      }
    }
  }

  return {
    isValid: missingFields.length === 0,
    missingFields,
  }
}

const IsFormCompletedModal: React.FC<IsFormCompletedModalProps> = ({
  open,
  onClose,
  title = 'Nurses Notes Form Incomplete',
  description = 'The Nurses Notes form is not fully completed. Please ensure all the the fields are filled before proceeding:',
  requiredFields = [],
  closeButtonText = 'Close',
  formData,
  formType = 'nursesNotes',
}) => {
  const validationResult = formData ? validateFormCompletion(formData, formType) : null
  const fieldsToShow = validationResult ? validationResult.missingFields : requiredFields

  const displayFields = fieldsToShow.slice(0, 5)
  const remainingCount = fieldsToShow.length - 5

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby='warning-dialog-title'
      aria-describedby='warning-dialog-description'
      maxWidth='sm'
      fullWidth
    >
      <DialogTitle id='warning-dialog-title' sx={{display: 'flex', alignItems: 'center', gap: 1}}>
        <Warning color='warning' />
        <Typography variant='h6' component='span'>
          {title}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <DialogContentText id='warning-dialog-description'>
          <Box sx={{mb: 2}}>
            <Typography variant='body1' sx={{mb: 1}}>
              {description}
            </Typography>
            {fieldsToShow.length > 0 && (
              <Box component='ul' sx={{pl: 2, mt: 1}}>
                {displayFields.map((field, index) => (
                  <Typography key={index} component='li' variant='body2'>
                    {field}
                  </Typography>
                ))}
                {remainingCount > 0 && (
                  <Typography
                    component='li'
                    variant='body2'
                    sx={{fontStyle: 'italic', color: 'text.secondary'}}
                  >
                    and {remainingCount} more field{remainingCount !== 1 ? 's' : ''}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
          <Typography variant='body1' color='warning.main' fontWeight='medium'>
            Please complete all the required fields before proceeding.
          </Typography>
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{p: 2}}>
        <Button onClick={onClose} variant='contained' color='primary'>
          {closeButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export {validateFormCompletion}
export default IsFormCompletedModal
